<script setup lang="ts">
import { useCurrentUser } from "@/api/current-user";
import { useCreateSpace } from "@/api/space-query";
import { useSnackbarStore } from "@/stores";
import { useScopeStore } from "@/stores/scope.ts";
import { mdiInformationOutline } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { Space, SpaceUpdate } from "@soft-db/monitoring-api-client";
import { Card, FormField, Icon, InputGroup, TextInput } from "@soft-db/ui";
import { useI18n } from "vue-i18n";
import { z } from "zod";
import AppDialog from "../common/AppDialog.vue";
import { icWorkspacesOutline } from "../icons";
import SpaceFeatureFlags from "../spaces/SpaceFeatureFlags.vue";

const emit = defineEmits<{
	created: [id: string];
}>();

const { t } = useI18n();

const dialog = defineModel<boolean>("dialog");

const { currentUser } = useCurrentUser();
const { overwriteDistId } = useScopeStore();

const spaceCreation = useCreateSpace();
const snackbarStore = useSnackbarStore();

const { useField, handleSubmit } = useForm<SpaceUpdate>();
const name = useField("name", { schema: z.string().trim().min(1) });
const { data: dashboard } = useField("features.dashboard", {
	isRequired: true,
	initialValue: true,
});
const { data: integrity } = useField("features.integrity", {
	isRequired: true,
	initialValue: true,
});
const { data: audit } = useField("features.audit", { isRequired: true, initialValue: true });

const { data: report } = useField("features.report", { isRequired: true, initialValue: true });

const createSpace = handleSubmit(async (form) => {
	if (!currentUser.value) return;
	if (currentUser.value.scope.type !== "distributor") return;

	await spaceCreation
		.mutateAsync({
			dist: overwriteDistId || currentUser.value.scope.id,
			space: {
				name: form.name,
				features: form.features,
			},
		})
		.then((space: Space) => {
			snackbarStore.openOnMessage(t("space.created"));
			emit("created", space.id);
			close();
		});
});

function close() {
	dialog.value = false;
}
</script>

<template>
	<AppDialog
		v-model="dialog"
		class="w-[500px]"
		:actionText="t('common.create')"
		@close="close"
		@click:action="createSpace"
	>
		<template #title>
			<Icon :d="icWorkspacesOutline" />
			<span>{{ t("space.createSpace") }}</span>
		</template>
		<template #content>
			<FormField class="w-full" :label="t('space.name')" :errors="name.errors.value">
				<InputGroup class="w-full">
					<TextInput v-model="name.data.value" />
				</InputGroup>
			</FormField>

			<span class="text-sm">{{ t("space.features") }}</span>
			<div class="mt-1 flex flex-col">
				<SpaceFeatureFlags
					v-model:dashboard="dashboard"
					v-model:integrity="integrity"
					v-model:report="report"
					v-model:audit="audit"
				/>
			</div>

			<Card class="bg-info/20 p-3">
				<div class="flex items-center gap-2">
					<Icon :d="mdiInformationOutline" size="lg" />
					{{ t("space.featuresAfterCreation") }}
				</div>
			</Card>
		</template>
	</AppDialog>
</template>
